import DOMPurify from "dompurify";

/**
 * Central HTML sanitiser for any CMS / database content that is rendered as
 * markup (listing descriptions, site content blocks, advert copy, …).
 *
 * Everything is stripped by default and only an explicit allow-list of safe
 * formatting tags survives — no <script>, <style>, <iframe>, event handlers,
 * inline styles, forms or embedded objects. This is the single chokepoint that
 * replaces ever passing raw server HTML straight to dangerouslySetInnerHTML.
 */

const ALLOWED_TAGS = [
  "p", "br", "hr", "span", "div",
  "b", "strong", "i", "em", "u", "s", "small", "mark", "sub", "sup",
  "ul", "ol", "li",
  "a",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "blockquote", "code", "pre",
  "table", "thead", "tbody", "tr", "th", "td",
  "figure", "figcaption", "img",
];

const ALLOWED_ATTR = [
  "href", "title", "target", "rel",
  "src", "alt", "width", "height", "loading",
  "colspan", "rowspan", "class",
];

const FORBID_TAGS = [
  "script", "style", "iframe", "object", "embed", "form", "input", "button",
  "textarea", "select", "option", "link", "meta", "base", "svg", "math",
  "template", "noscript", "canvas", "audio", "video", "source", "track",
];

const FORBID_ATTR = [
  "style", "onerror", "onload", "onclick", "onmouseover", "onmouseenter",
  "onfocus", "onblur", "formaction", "srcdoc", "srcset", "xlink:href",
  "xmlns", "action", "method",
];

/**
 * Returns a safe HTML string. Any element/attribute outside the allow-list is
 * removed (not escaped), and javascript:/data: URLs are neutralised by
 * DOMPurify's default URI policy.
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return "";
  return DOMPurify.sanitize(String(dirty), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: true,
    KEEP_CONTENT: true,
  });
}

/** True when the string actually contains markup worth rendering as HTML. */
export function hasMarkup(value: string | null | undefined): boolean {
  return typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value);
}

/**
 * Renders a plain string as safe HTML — newlines become <br /> and the result
 * still flows through the sanitiser, so mixed plain/rich content is safe.
 */
export function toSafeHtml(value: string | null | undefined): string {
  if (!value) return "";
  if (hasMarkup(value)) return sanitizeHtml(value);
  const escaped = value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br />");
  return sanitizeHtml(escaped);
}